import {match} from 'ts-pattern';

import {groupBy, toPairs} from 'ramda';

import {ContextTemplateResponseBody} from '@dms/api/core';
import {i18n} from '@dms/i18n';

import {Nullish} from 'shared/web';

import {getLocaleLabel} from '../utils/getLocaleLabel';
import {useTenant} from './useTenant';

const OTHERS_KEY = '__others__';

export type TemplateGroup = {
  key: string;
  label: string;
  templates: ContextTemplateResponseBody[];
};

export type UseTemplatesByLocaleResult =
  | {isGrouped: false; templates: ContextTemplateResponseBody[]}
  | {isGrouped: true; groups: TemplateGroup[]};

export function useTemplatesByLocale(
  templates: ContextTemplateResponseBody[] | undefined,
  preferredLocale?: string | Nullish
): UseTemplatesByLocaleResult {
  const {isMultiLocale} = useTenant();
  const list = templates ?? [];

  if (!isMultiLocale) {
    return {isGrouped: false, templates: list};
  }

  const grouped = groupBy<ContextTemplateResponseBody>(
    (template) => template.locale ?? OTHERS_KEY,
    list
  );

  const groups: TemplateGroup[] = toPairs(grouped).map(([key, templatesInGroup]) => {
    const baseLabel =
      key === OTHERS_KEY ? i18n.t('general.labels.others') : (getLocaleLabel(key) ?? key);
    const label =
      key === preferredLocale ? `${baseLabel} (${i18n.t('general.labels.preferred')})` : baseLabel;

    return {
      key,
      label,
      templates: templatesInGroup ?? [],
    };
  });

  const sortedGroups = [...groups].sort((a, b) =>
    match([a.key === preferredLocale, b.key === preferredLocale] as const)
      .with([true, false], () => -1)
      .with([false, true], () => 1)
      .otherwise(() => 0)
  );

  return {isGrouped: true, groups: sortedGroups};
}
